import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, ArrowRight, Info, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { PRICING, EXTRA_SERVICES } from "../../mock";
import { format, parseISO } from "date-fns";
import Reveal from "./Reveal";
import { SectionHead } from "./Services";

export const openForm = (mode) => {
  window.dispatchEvent(new CustomEvent("evt:form-mode", { detail: mode }));
  document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
};

export const selectService = (service) => {
  window.dispatchEvent(new CustomEvent("evt:select-service", { detail: service }));
  document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
};

const fmt = (n) => (Number.isInteger(Number(n)) ? Number(n) : Number(n).toFixed(2));

const Pricing = () => {
  const [active, setActive] = useState(null);
  const [saved, setSaved] = useState({});
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/packages`)
      .then(({ data }) => setSaved(Object.fromEntries(data.map((d) => [d.service, d]))))
      .catch(() => {});
  }, []);
  // Owner edits from the admin page override the built-in text; price/CTA stay as defined
  const packages = PRICING.map((p) => {
    const s = saved[p.service];
    return s ? { ...p, price: s.price, was: s.was_price, offer: s.offer, features: s.features, details: { tagline: s.tagline, intro: s.intro, benefits: s.benefits, note: s.note } } : p;
  });
  const book = (p) => { setActive(null); setTimeout(() => selectService(p.service), 150); };
  return (
  <section id="pricing" className="bg-[#08090b] py-24 lg:py-28">
    <div className="container-x">
      <SectionHead badge="Pricing" title="Clear, Honest Pricing" sub="Transparent starting prices. Your final quote depends on your vehicle." />
      <div className="grid gap-6 md:grid-cols-3">
        {packages.map((p, i) => (
          <Reveal key={p.name} delay={i * 100}>
            <div
              className={`card-hover relative flex h-full flex-col rounded-2xl border bg-[#111217] p-8 ${p.popular ? "border-brand shadow-[0_20px_50px_-20px_rgba(239,28,37,.45)]" : "border-white/10"}`}
              data-testid={`pricing-card-${i}`}
            >
              {p.offer && (
                <span className="absolute -top-3 left-6 rounded-md bg-[#FFC439] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-black" data-testid={`pricing-offer-badge-${i}`}>{p.offer.name}</span>
              )}
              {p.popular && (
                <span className="absolute -top-3 right-6 rounded-md bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-white">Popular</span>
              )}
              <h3 className="font-display text-[30px] font-bold uppercase leading-none text-white">{p.name}</h3>
              <div className="mt-6 text-[12px] font-medium uppercase tracking-[0.1em] text-white/50">From{p.was && <span className="ml-2 text-[15px] normal-case tracking-normal text-white/40 line-through" data-testid={`pricing-was-${i}`}>£{fmt(p.was)}</span>}</div>
              <div className="mt-1 font-display text-[60px] font-bold leading-none text-white" data-testid={`pricing-price-${i}`}>£{fmt(p.price)}</div>
              {p.offer && <div className="mt-2 text-[12px] font-semibold text-[#FFC439]" data-testid={`pricing-offer-ends-${i}`}>Save £{fmt(p.was - p.price)} · ends {format(parseISO(p.offer.end_date), "d MMM")}</div>}
              <div className="mt-2 text-[13px] text-white/55" data-testid={`pricing-monthly-${i}`}>
                or 3 × <span className="font-semibold text-white">£{(p.price / 3).toFixed(2)}</span> interest-free with <span className="font-semibold text-[#FFA8CD]">Klarna</span> or <span className="font-semibold text-[#FFC439]">PayPal</span>
              </div>
              <ul className="mt-7 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 border-b border-white/10 py-3 text-[15px] text-white/85">
                    <Check className="h-4 w-4 shrink-0 text-brand" strokeWidth={3} />{f}
                  </li>
                ))}
              </ul>
              {p.details && (
                <button onClick={() => setActive(p)} className="mt-6 flex w-fit items-center gap-1.5 text-[14px] font-semibold text-white/70 underline-offset-4 transition-colors hover:text-brand hover:underline" data-testid={`pricing-details-${i}`}>
                  <Info className="h-4 w-4" />View details
                </button>
              )}
              <button
                onClick={() => selectService(p.service)}
                className="btn-red group mt-4 flex w-fit items-center gap-2 rounded-lg px-6 py-3.5 text-[15px] font-bold uppercase tracking-[0.04em]"
                data-testid={`pricing-cta-${i}`}
              >
                {p.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={150}>
        <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-[#111217]" data-testid="extra-services-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-left">
              <thead className="bg-[#16171d]">
                <tr className="text-[12px] font-semibold uppercase tracking-[0.08em] text-white/80">
                  <th className="px-6 py-4">Additional Service</th>
                  <th className="px-6 py-4">Starting Price</th>
                  <th className="px-6 py-4">Notes</th>
                  <th className="px-6 py-4" />
                </tr>
              </thead>
              <tbody>
                {EXTRA_SERVICES.map((s, i) => (
                  <tr key={s.name} className="border-t border-white/10 transition-colors hover:bg-white/[0.03]" data-testid={`extra-row-${i}`}>
                    <td className="px-6 py-5 text-[15px] text-white/85">{s.name}</td>
                    <td className="px-6 py-5 font-display text-lg font-bold text-white">{s.price}</td>
                    <td className="px-6 py-5 text-[15px] font-medium text-white/70">{s.notes}</td>
                    <td className="px-6 py-5 text-right">
                      <button onClick={() => selectService(s.service)} className="text-sm font-semibold text-brand transition-colors hover:text-white" data-testid={`extra-enquire-${i}`}>Enquire</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Reveal>
    </div>

    <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
      <DialogContent className={`max-h-[90vh] max-w-xl overflow-y-auto border bg-[#111217] p-0 text-white ${active?.popular ? "border-brand" : "border-white/10"}`} data-testid="package-dialog">
        {active && (
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4 pr-6">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-brand" data-testid="package-tagline">{active.details.tagline}</span>
                  {active.popular && <span className="rounded-md bg-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-white">Popular</span>}
                </div>
                <DialogTitle className="mt-2 font-display text-[34px] font-bold uppercase leading-none text-white" data-testid="package-title">{active.name}</DialogTitle>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-medium uppercase tracking-[0.1em] text-white/50">{active.offer ? active.offer.name : "From"}</div>
                {active.was && <div className="text-[15px] text-white/40 line-through" data-testid="package-was-price">£{fmt(active.was)}</div>}
                <div className="font-display text-[44px] font-bold leading-none text-white" data-testid="package-price">£{fmt(active.price)}</div>
              </div>
            </div>
            <DialogDescription className="mt-5 text-[15.5px] leading-relaxed text-white/75" data-testid="package-intro">{active.details.intro}</DialogDescription>
            <div className="mt-6 text-[12px] font-semibold uppercase tracking-[0.1em] text-white/50">Key benefits</div>
            <ul className="mt-2">
              {active.details.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 border-b border-white/10 py-3 text-[15px] text-white/85">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{b}
                </li>
              ))}
            </ul>
            <p className="mt-5 rounded-lg border border-white/10 bg-[#0b0c0f] p-3 text-[13px] leading-relaxed text-white/55" data-testid="package-note">{active.details.note}</p>
            <div className="mt-3 text-[13px] text-white/55">
              or 3 × <span className="font-semibold text-white">£{(active.price / 3).toFixed(2)}</span> interest-free with <span className="font-semibold text-[#FFA8CD]">Klarna</span> or <span className="font-semibold text-[#FFC439]">PayPal</span>
            </div>
            <div className="sticky bottom-0 -mx-6 mt-6 bg-[#111217] px-6 pb-1 pt-2 sm:-mx-8 sm:px-8">
              <button onClick={() => book(active)} className="btn-red group flex w-full items-center justify-center gap-2 rounded-lg py-4 text-[15px] font-bold uppercase tracking-[0.04em]" data-testid="package-book-btn">
                Book {active.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </section>
  );
};

export default Pricing;
